import { addYears, subYears } from 'date-fns';
import { adminBookingsGetList } from '@pro-sto/api-client';
import type { AdminBookingResponseDto } from '@pro-sto/api-client';

const isClientAppBooking = (booking: AdminBookingResponseDto, clientUuid: string): boolean => {
  if (booking.status === 'cancelled') return false;
  if (booking.created_by_source !== 'client_app') return false;
  const uuid = booking.client?.uuid ?? booking.client_uuid;
  return uuid === clientUuid;
};

const PAGE_SIZE = 100;
const MAX_PAGES = 20;

/** Склеивает списки записей без дублей по uuid. */
export const mergeBookingsByUuid = (
  ...lists: AdminBookingResponseDto[][]
): AdminBookingResponseDto[] => {
  const byUuid = new Map<string, AdminBookingResponseDto>();
  for (const list of lists) {
    for (const booking of list) {
      if (!byUuid.has(booking.uuid)) {
        byUuid.set(booking.uuid, booking);
      }
    }
  }
  return [...byUuid.values()];
};

/**
 * Загружает записи за интервал без фильтра статусов и без привязки к срезу календаря.
 * Пагинирует, пока не получит все страницы или не упрётся в MAX_PAGES.
 */
export const fetchBookingsInRange = async (params: {
  serviceCenterUuid: string;
  dateFrom: Date;
  dateTo: Date;
}): Promise<AdminBookingResponseDto[]> => {
  const all: AdminBookingResponseDto[] = [];
  let offset = 0;

  for (let page = 0; page < MAX_PAGES; page += 1) {
    const response = await adminBookingsGetList({
      serviceCenterUuid: params.serviceCenterUuid,
      dateFrom: params.dateFrom.toISOString(),
      dateTo: params.dateTo.toISOString(),
      limit: PAGE_SIZE,
      offset,
    });
    const data = response.data ?? [];
    all.push(...data);
    offset += data.length;
    if (data.length === 0 || offset >= response.total) {
      break;
    }
  }

  return all;
};

/**
 * Есть ли у клиента активные записи из приложения — по API, а не по срезу календаря.
 * Останавливается, как только находит такую запись.
 */
export const fetchClientHasAppBookings = async (
  serviceCenterUuid: string,
  clientUuid: string,
): Promise<boolean> => {
  const dateFrom = subYears(new Date(), 5);
  const dateTo = addYears(new Date(), 1);
  let offset = 0;

  for (let page = 0; page < MAX_PAGES; page += 1) {
    const response = await adminBookingsGetList({
      serviceCenterUuid,
      dateFrom: dateFrom.toISOString(),
      dateTo: dateTo.toISOString(),
      limit: PAGE_SIZE,
      offset,
    });
    const data = response.data ?? [];
    if (data.some((booking) => isClientAppBooking(booking, clientUuid))) {
      return true;
    }
    offset += data.length;
    if (data.length === 0 || offset >= response.total) {
      break;
    }
  }

  return false;
};
